import type { OBB, Vec2 } from '../../../geo/types';
import { obbCorners } from '../../../geo/polygon';
import type { Lot } from '../types';
import type { LotPlacer } from '../lotPlacer';
import type { WoodcutGround } from './woodcutGround';

/**
 * PLAN Lots, the buildings the woodcut draws where no plot built (2026-10-05): ground it draws
 * surely built, free of every house, plot building, street and reservation, in patches at least
 * `least` metres across and `patch` square metres. A patch is built as its nearest house within `reach`
 * stands, in buildings about `frontage` by `depth`, each where the woodcut roofs `cover` of it.
 * The woodcut says a roof stood there, not how many or whose: the division is the model's.
 */
export const DRAWN = {
  least: 4,
  patch: 40,
  frontage: 5.5,
  depth: 8,
  cover: 0.6,
  reach: 40,
  inset: 0.25,
} as const;

interface Patch {
  readonly cells: readonly Vec2[];
  readonly centre: Vec2;
}

/* The surely built cells a building could stand on, less those in no square `least` metres wide. */
function freeCells(ground: WoodcutGround, placer: LotPlacer): Uint8Array {
  const { cols, rows, cell } = ground.grid;
  const free = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      if (!ground.surelyBuilt(c, r)) continue;
      const box: OBB = {
        center: ground.centreOf(c, r),
        axisU: [1, 0],
        halfU: cell / 2,
        halfV: cell / 2,
      };
      if (placer.fits(box)) free[r * cols + c] = 1;
    }
  const span = Math.max(1, Math.round(DRAWN.least / cell));
  const wide = new Uint8Array(cols * rows);
  for (let r = 0; r + span <= rows; r++)
    for (let c = 0; c + span <= cols; c++) {
      let all = true;
      for (let dr = 0; dr < span && all; dr++)
        for (let dc = 0; dc < span && all; dc++) all = free[(r + dr) * cols + c + dc] === 1;
      if (!all) continue;
      for (let dr = 0; dr < span; dr++)
        for (let dc = 0; dc < span; dc++) wide[(r + dr) * cols + c + dc] = 1;
    }
  return wide;
}

/* The cells joined side to side, in the order their first cells lie in the grid. */
function patchesOf(free: Uint8Array, ground: WoodcutGround): Patch[] {
  const { cols, rows, cell } = ground.grid;
  const seen = new Uint8Array(free.length);
  const patches: Patch[] = [];
  for (let start = 0; start < free.length; start++) {
    if (!free[start] || seen[start]) continue;
    const members = [start];
    seen[start] = 1;
    for (let i = 0; i < members.length; i++) {
      const k = members[i]!;
      const c = k % cols;
      const r = (k - c) / cols;
      const next = [
        c > 0 ? k - 1 : -1,
        c < cols - 1 ? k + 1 : -1,
        r > 0 ? k - cols : -1,
        r < rows - 1 ? k + cols : -1,
      ];
      for (const n of next)
        if (n >= 0 && free[n] && !seen[n]) {
          seen[n] = 1;
          members.push(n);
        }
    }
    if (members.length * cell * cell < DRAWN.patch) continue;
    const cells = members.map((k) => ground.centreOf(k % cols, Math.floor(k / cols)));
    patches.push({
      cells,
      centre: [
        cells.reduce((sum, p) => sum + p[0], 0) / cells.length,
        cells.reduce((sum, p) => sum + p[1], 0) / cells.length,
      ],
    });
  }
  return patches;
}

function nearestHouse(houses: readonly Lot[], at: Vec2): Lot | null {
  let best: Lot | null = null;
  let least: number = DRAWN.reach;
  for (const house of houses) {
    const d = Math.hypot(house.obb.center[0] - at[0], house.obb.center[1] - at[1]);
    if (d < least) {
      least = d;
      best = house;
    }
  }
  return best;
}

/* The buildings over a patch, square to `house`: one per tile of its frame the woodcut roofs enough of. */
function tilesOf(patch: Patch, house: Lot, cell: number): OBB[] {
  const across = house.obb.axisU;
  const back: Vec2 = [-house.facing[0], -house.facing[1]];
  const local = patch.cells.map((p): Vec2 => [
    (p[0] - patch.centre[0]) * across[0] + (p[1] - patch.centre[1]) * across[1],
    (p[0] - patch.centre[0]) * back[0] + (p[1] - patch.centre[1]) * back[1],
  ]);
  const u0 = Math.min(...local.map((p) => p[0])) - cell / 2;
  const u1 = Math.max(...local.map((p) => p[0])) + cell / 2;
  const t0 = Math.min(...local.map((p) => p[1])) - cell / 2;
  const t1 = Math.max(...local.map((p) => p[1])) + cell / 2;
  const nu = Math.max(1, Math.round((u1 - u0) / DRAWN.frontage));
  const nt = Math.max(1, Math.round((t1 - t0) / DRAWN.depth));
  const boxes: OBB[] = [];
  for (let j = 0; j < nt; j++)
    for (let i = 0; i < nu; i++) {
      const inTile = local.filter(
        (p) =>
          Math.min(nu - 1, Math.floor(((p[0] - u0) / (u1 - u0)) * nu)) === i &&
          Math.min(nt - 1, Math.floor(((p[1] - t0) / (t1 - t0)) * nt)) === j
      );
      if (inTile.length === 0) continue;
      const a0 = Math.min(...inTile.map((p) => p[0])) - cell / 2 + DRAWN.inset;
      const a1 = Math.max(...inTile.map((p) => p[0])) + cell / 2 - DRAWN.inset;
      const b0 = Math.min(...inTile.map((p) => p[1])) - cell / 2 + DRAWN.inset;
      const b1 = Math.max(...inTile.map((p) => p[1])) + cell / 2 - DRAWN.inset;
      const width = a1 - a0;
      const depth = b1 - b0;
      if (width < DRAWN.least - 2 * DRAWN.inset || depth < DRAWN.least - 2 * DRAWN.inset) continue;
      if (inTile.length * cell * cell < DRAWN.cover * width * depth) continue;
      const uc = (a0 + a1) / 2;
      const tc = (b0 + b1) / 2;
      boxes.push({
        center: [
          patch.centre[0] + across[0] * uc + back[0] * tc,
          patch.centre[1] + across[1] * uc + back[1] * tc,
        ],
        axisU: across,
        halfU: width / 2,
        halfV: depth / 2,
      });
    }
  return boxes;
}

/**
 * The buildings on the ground the woodcut draws built and nothing planned stands on, numbered from
 * `startIndex` and named after the house each stands as; the placer learns each one.
 */
export function drawnBuildings(
  houses: readonly Lot[],
  ground: WoodcutGround,
  placer: LotPlacer,
  startIndex: number
): Lot[] {
  const lots: Lot[] = [];
  const count = new Map<Lot, number>();
  for (const patch of patchesOf(freeCells(ground, placer), ground)) {
    const house = nearestHouse(houses, patch.centre);
    if (!house) continue;
    for (const obb of tilesOf(patch, house, ground.grid.cell)) {
      if (!placer.fits(obb)) continue;
      const k = (count.get(house) ?? 0) + 1;
      count.set(house, k);
      const lot: Lot = {
        index: startIndex + lots.length,
        sourceId: `${house.sourceId}/drawn${k}`,
        side: house.side,
        ordinal: house.ordinal,
        kind: 'backhouse',
        obb,
        corners: obbCorners(obb),
        frontage: obb.halfU * 2,
        depth: obb.halfV * 2,
        facing: house.facing,
      };
      lots.push(lot);
      placer.add(lot);
    }
  }
  return lots;
}
